import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Chip,
  Stack,
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  Rocket as RocketIcon,
  Security as SecurityIcon,
  Support as SupportIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PageSEO from '../components/PageSEO';
import Header from '../components/Header';
import Footer from '../components/Footer';

const AboutPage: React.FC = () => {
  const { t } = useTranslation();


  const values = [
    {
      icon: <RocketIcon />,
      title: t('about.innovation'),
      description: t('about.innovationDesc'),
    },
    {
      icon: <SecurityIcon />,
      title: t('about.security'),
      description: t('about.securityDesc'),
    },
    {
      icon: <SupportIcon />,
      title: t('about.support'),
      description: t('about.supportDesc'),
    },
    {
      icon: <PeopleIcon />,
      title: t('about.community'),
      description: t('about.communityDesc'),
    },
  ];


  return (
    <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh' }}>
      <PageSEO pageKey="about" />
      <Header />
      
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              color: '#ffffff',
              fontWeight: 800,
              mb: 3,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              lineHeight: { xs: 1.2, md: 1.3 },
              background: 'linear-gradient(135deg, #ffffff 0%, #28A0F0 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
{t('about.title')}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#a0a0a0',
              mb: 4,
              maxWidth: 800,
              mx: 'auto',
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              lineHeight: { xs: 1.4, md: 1.5 },
              px: { xs: 2, sm: 0 },
            }}
          >
{t('about.subtitle')}
          </Typography>
        </Box>

        {/* Mission Section */}
        <Box sx={{ mb: { xs: 6, md: 8 } }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h4"
                sx={{
                  color: '#ffffff',
                  fontWeight: 700,
                  mb: 3,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                }}
              >
{t('about.mission')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#d0d0d0',
                  mb: 3,
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  lineHeight: 1.6,
                }}
              >
{t('about.missionText1')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#d0d0d0',
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  lineHeight: 1.6,
                }}
              >
{t('about.missionText2')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
                  border: '1px solid rgba(40, 160, 240, 0.3)',
                  borderRadius: 3,
                  p: 4,
                  textAlign: 'center',
                }}
              >
                <BusinessIcon sx={{ fontSize: 60, color: '#ffffff', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
{t('about.createdIn')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
{t('about.createdInDesc')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>


        {/* Values Section */}
        <Box sx={{ mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h4"
            sx={{
              color: '#ffffff',
              fontWeight: 700,
              mb: 4,
              textAlign: 'center',
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
{t('about.values')}
          </Typography>
          <Grid container spacing={3}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: 3,
                    p: 3,
                    height: '100%',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(40, 160, 240, 0.2)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      color: '#ffffff',
                      mb: 2,
                      '& .MuiSvgIcon-root': {
                        fontSize: { xs: 40, md: 48 },
                      },
                    }}
                  >
                    {value.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#ffffff',
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { xs: '1rem', md: '1.25rem' },
                    }}
                  >
                    {value.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#a0a0a0',
                      fontSize: { xs: '0.8rem', md: '0.875rem' },
                      lineHeight: 1.5,
                    }}
                  >
                    {value.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>


        {/* CTA Section */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
            border: '1px solid rgba(40, 160, 240, 0.3)',
            borderRadius: 4,
            p: { xs: 4, md: 6 },
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: '#ffffff',
              fontWeight: 700,
              mb: 3,
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
{t('about.readyToStart')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#a0a0a0',
              mb: 4,
              maxWidth: 600,
              mx: 'auto',
              fontSize: { xs: '0.9rem', md: '1rem' },
              lineHeight: 1.6,
            }}
          >
{t('about.readyToStartDesc')}
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Chip
              icon={<CheckCircleIcon />}
              label={t('about.freeRegistration')}
              sx={{
                backgroundColor: 'rgba(16, 185, 129, 0.2)',
                color: '#10b981',
                border: '1px solid #10b981',
                fontWeight: 600,
              }}
            />
            <Chip
              icon={<StarIcon />}
              label={t('about.highQuality')}
              sx={{
                backgroundColor: 'rgba(245, 158, 11, 0.2)',
                color: '#f59e0b',
                border: '1px solid #f59e0b',
                fontWeight: 600,
              }}
            />
            <Chip
              icon={<TrendingUpIcon />}
              label={t('about.growthAndDevelopment')}
              sx={{
                backgroundColor: 'rgba(40, 160, 240, 0.2)',
                color: '#28A0F0',
                border: '1px solid #28A0F0',
                fontWeight: 600,
              }}
            />
          </Stack>
        </Box>
      </Container>

      <Footer />
    </Box>
  );
};

export default AboutPage;
